import { useLingui } from "@lingui/react/macro";
import type { MessageId } from "@t3tools/contracts";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

import { cn } from "~/lib/utils";

import { isUserMessageRailVisible, type UserMessageRailEntry } from "./userMessageRail.logic";

/** The last user message whose top sits within this share of the viewport from its top is the current one. */
const CURRENT_LINE_RATIO = 0.15;
const PREVIEW_GAP_PX = 8;
const PREVIEW_EDGE_PX = 8;

function findScroller(root: HTMLElement | null): HTMLElement | null {
  return root?.querySelector<HTMLElement>("[data-timeline-scroll]") ?? null;
}

function currentMessageId(
  scroller: HTMLElement,
  entries: ReadonlyArray<UserMessageRailEntry>,
): MessageId | null {
  const line = scroller.scrollTop + scroller.clientHeight * CURRENT_LINE_RATIO;
  let current: MessageId | null = null;
  for (const entry of entries) {
    const row = scroller.querySelector<HTMLElement>(
      `[data-message-id="${CSS.escape(entry.messageId)}"]`,
    );
    if (!row) continue;
    if (current === null || row.offsetTop <= line) {
      current = entry.messageId;
      continue;
    }
    break;
  }
  return current;
}

function revealTick(rail: HTMLElement, tick: HTMLElement): void {
  const top = tick.offsetTop;
  const bottom = top + tick.offsetHeight;
  if (top < rail.scrollTop) {
    rail.scrollTop = top;
  } else if (bottom > rail.scrollTop + rail.clientHeight) {
    rail.scrollTop = bottom - rail.clientHeight;
  }
}

interface PreviewPlacement {
  top: number;
  left: number;
}

/** Places the preview beside the rail, centred on the hovered tick and kept inside the wrapper. */
function placePreview(
  rail: HTMLElement,
  tick: HTMLElement,
  panel: HTMLElement,
): PreviewPlacement | null {
  const wrapper = rail.offsetParent;
  if (!(wrapper instanceof HTMLElement)) return null;
  const wrapperRect = wrapper.getBoundingClientRect();
  const railRect = rail.getBoundingClientRect();
  const tickRect = tick.getBoundingClientRect();
  const centre = tickRect.top + tickRect.height / 2 - wrapperRect.top;
  const maxTop = Math.max(
    PREVIEW_EDGE_PX,
    wrapperRect.height - panel.offsetHeight - PREVIEW_EDGE_PX,
  );
  return {
    top: Math.min(Math.max(centre - panel.offsetHeight / 2, PREVIEW_EDGE_PX), maxTop),
    left: railRect.right - wrapperRect.left + PREVIEW_GAP_PX,
  };
}

export function UserMessageRail({
  entries,
  scrollerRootRef,
  threadKey,
  onSelect,
}: {
  entries: ReadonlyArray<UserMessageRailEntry>;
  scrollerRootRef: RefObject<HTMLElement | null>;
  threadKey: string;
  onSelect: (messageId: MessageId) => void;
}) {
  const { t } = useLingui();
  const railRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [currentId, setCurrentId] = useState<MessageId | null>(null);
  const [hoveredId, setHoveredId] = useState<MessageId | null>(null);
  const [placement, setPlacement] = useState<PreviewPlacement | null>(null);
  const visible = isUserMessageRailVisible(entries);
  const hovered =
    hoveredId === null ? null : (entries.find((entry) => entry.messageId === hoveredId) ?? null);

  const hoverOut = useCallback(() => setHoveredId(null), []);

  useEffect(() => {
    if (!visible) return;
    const scroller = findScroller(scrollerRootRef.current);
    if (!scroller) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      setCurrentId(currentMessageId(scroller, entries));
    };
    const schedule = () => {
      if (frame === 0) {
        frame = window.requestAnimationFrame(update);
      }
    };
    update();
    scroller.addEventListener("scroll", schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(scroller);
    return () => {
      scroller.removeEventListener("scroll", schedule);
      observer.disconnect();
      if (frame !== 0) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [entries, scrollerRootRef, threadKey, visible]);

  useEffect(() => {
    const rail = railRef.current;
    const tick = rail?.querySelector<HTMLElement>('[data-current="true"]');
    if (rail && tick) {
      revealTick(rail, tick);
    }
  }, [currentId]);

  useLayoutEffect(() => {
    const rail = railRef.current;
    const panel = panelRef.current;
    if (hovered === null || !rail || !panel) {
      setPlacement(null);
      return;
    }
    const place = () => {
      const tick = rail.querySelector<HTMLElement>(
        `[data-user-message-tick="${CSS.escape(hovered.messageId)}"]`,
      );
      if (tick) {
        setPlacement(placePreview(rail, tick, panel));
      }
    };
    place();
    rail.addEventListener("scroll", place, { passive: true });
    const observer = new ResizeObserver(place);
    observer.observe(panel);
    return () => {
      rail.removeEventListener("scroll", place);
      observer.disconnect();
    };
  }, [hovered]);

  if (!visible) {
    return null;
  }

  return (
    <>
      <nav
        ref={railRef}
        aria-label={t`User messages`}
        data-user-message-rail="true"
        className="absolute top-1/2 left-2 z-20 hidden max-h-[70%] -translate-y-1/2 flex-col overflow-y-auto py-1 [scrollbar-width:none] md:flex"
        onMouseLeave={hoverOut}
      >
        {entries.map((entry) => {
          const isHovered = entry.messageId === hoveredId;
          const isCurrent = entry.messageId === currentId;
          const black = hoveredId === null ? isCurrent : isHovered;
          return (
            <button
              key={entry.messageId}
              type="button"
              data-user-message-tick={entry.messageId}
              data-current={isCurrent ? "true" : undefined}
              data-tick-tone={black ? "black" : "muted"}
              aria-current={isCurrent ? "true" : undefined}
              aria-label={t`Jump to message`}
              className="flex h-2 w-8 cursor-pointer items-center outline-none"
              onMouseEnter={() => setHoveredId(entry.messageId)}
              onFocus={(event) => {
                if (event.currentTarget.matches(":focus-visible")) {
                  setHoveredId(entry.messageId);
                }
              }}
              onBlur={hoverOut}
              onClick={() => onSelect(entry.messageId)}
            >
              <span
                className={cn(
                  "h-0.5 rounded-full transition-all duration-150",
                  isHovered ? "w-6" : "w-2.5",
                  black ? "bg-foreground" : "bg-muted-foreground/35",
                )}
              />
            </button>
          );
        })}
      </nav>
      {hovered !== null ? (
        <div
          ref={panelRef}
          role="status"
          data-user-message-preview={hovered.messageId}
          className={cn(
            "pointer-events-none absolute z-30 w-72 rounded-lg border bg-popover px-3 py-2 text-popover-foreground shadow-lg/5 transition-[top] duration-150 ease-out",
            placement === null && "invisible",
          )}
          style={placement ?? undefined}
        >
          <div key={hovered.messageId} className="rail-preview-swap">
            <p className="truncate text-xs font-semibold text-foreground">
              {hovered.title.length > 0 ? hovered.title : t`Empty message`}
            </p>
            {hovered.answer !== null ? (
              <p className="mt-1.5 line-clamp-4 text-xs whitespace-pre-line text-muted-foreground">
                {hovered.answer}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
